import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useBlocker, useLocation, useNavigate, useParams } from "react-router";
import { StatusBadge } from "@/components/shared/status-badge";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetApiAgentsAgentId } from "@/lib/api/generated/agents/agents";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { ROUTES } from "@/app/routes";
import type { ControlPlaneAgentRecord } from "@/lib/types/control-plane-agents";
import {
  useAgentEditorUiStore,
  type AgentEditorTabId,
} from "@/stores/agent-editor-ui-store";

const AGENT_TABS = [
  { label: "Overview", path: "", tabId: undefined },
  { label: "Persona", path: "/persona", tabId: "persona" as AgentEditorTabId },
  { label: "Instructions", path: "/instructions", tabId: "instructions" as AgentEditorTabId },
  { label: "Identity", path: "/identity", tabId: "identity" as AgentEditorTabId },
  { label: "Policy", path: "/policy", tabId: "policy" as AgentEditorTabId },
  { label: "Skills", path: "/skills", tabId: undefined },
  { label: "Access", path: "/access", tabId: "access" as AgentEditorTabId },
  { label: "Deployments", path: "/deployments", tabId: undefined },
] as const;

export function AgentLayout() {
  const { agentId } = useParams<{ agentId: string }>();
  const { tenantId: resolvedTenantId } = useTenantContext();
  const { data: agent } = useGetApiAgentsAgentId<ControlPlaneAgentRecord>(agentId!, {
    query: { enabled: Boolean(resolvedTenantId && agentId) },
  });
  const basePath = ROUTES.tenantAgent(resolvedTenantId, agentId!);

  return (
    <AgentLayoutInner basePath={basePath} agentName={agent?.name ?? agentId!} agentStatus={agent?.status} />
  );
}

function AgentLayoutInner({
  basePath,
  agentName,
  agentStatus,
}: {
  basePath: string;
  agentName: string;
  agentStatus?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const dirtyTabs = useAgentEditorUiStore((s) => s.dirtyTabs);
  const hasDirtyTabs = useAgentEditorUiStore((s) => s.hasDirtyTabs);

  const currentTabPath = useMemo(() => {
    for (const tab of AGENT_TABS) {
      if (tab.path === "") {
        if (location.pathname === basePath) {return tab.path;}
        continue;
      }
      if (location.pathname.startsWith(basePath + tab.path)) {
        return tab.path;
      }
    }
    return "";
  }, [location.pathname, basePath]);

  // Determine which tabId is currently active
  const currentTabId = useMemo(() => {
    return AGENT_TABS.find((tab) => tab.path === currentTabPath)?.tabId;
  }, [currentTabPath]);

  const isCurrentTabDirty = currentTabId ? dirtyTabs.has(currentTabId) : false;

  // --- Tab-to-tab navigation guard ---
  const [pendingTabPath, setPendingTabPath] = useState<string | null>(null);

  const handleTabChange = useCallback(
    (tabPath: string) => {
      const targetPath = basePath + tabPath;
      if (targetPath === location.pathname) {return;} // same tab
      if (isCurrentTabDirty) {
        setPendingTabPath(targetPath);
        return;
      }
      navigate(targetPath);
    },
    [basePath, location.pathname, navigate, isCurrentTabDirty],
  );

  // --- Leave-editor navigation guard ---
  const blocker = useBlocker(
    useCallback(
      ({ nextLocation }) => {
        if (!hasDirtyTabs) {return false;}
        // Allow navigation within the agent editor tabs
        if (nextLocation.pathname.startsWith(basePath)) {return false;}
        return true;
      },
      [hasDirtyTabs, basePath],
    ),
  );

  // --- Browser beforeunload ---
  useEffect(() => {
    if (!hasDirtyTabs) {return;}
    function handler(e: BeforeUnloadEvent) {
      e.preventDefault();
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDirtyTabs]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-0">
      {/* Agent header */}
      <div className="mb-0 flex shrink-0 items-center gap-3 pb-3">
        <h2 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
          {agentName}
        </h2>
        {agentStatus && <StatusBadge status={agentStatus} />}
      </div>

      {/* Tab nav */}
      <Tabs value={currentTabPath} onValueChange={handleTabChange} className="mb-4 shrink-0 overflow-x-auto">
        <TabsList>
          {AGENT_TABS.map((tab) => {
            const isDirty = tab.tabId ? dirtyTabs.has(tab.tabId) : false;
            return (
              <TabsTrigger key={tab.path} value={tab.path} className="gap-1.5">
                {tab.label}
                {isDirty && (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--warn)]" />
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>

      {/* Tab-to-tab confirm dialog */}
      <ConfirmDialog
        open={pendingTabPath !== null}
        onConfirm={() => {
          const path = pendingTabPath;
          setPendingTabPath(null);
          if (path) {navigate(path);}
        }}
        onCancel={() => setPendingTabPath(null)}
        title="Unsaved changes"
        description="You have unsaved changes on this tab. Discard and switch?"
        confirmLabel="Discard & Switch"
        variant="destructive"
      />

      {/* Leave-editor confirm dialog */}
      <ConfirmDialog
        open={blocker.state === "blocked"}
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
        title="Unsaved changes"
        description="You have unsaved changes in the agent editor. Leave without saving?"
        confirmLabel="Leave"
        variant="destructive"
      />
    </div>
  );
}
